/**
 * Agenda Summary List
 * The Agenda layout's "Day summaries only" mode: one row per day with the day heading on the left
 * and the organizer's summary on the right (stacked on narrow widths). For a high-level agenda
 * before session times are set.
 */

import type React from "react";

import type { AgendaDay } from "../../../../_logic/utils/schedule-agenda";
import { formatScheduleDayHeading } from "../../../../_logic/utils/schedule-display-format";

interface AgendaSummaryListProps {
  /** Days to list; days without a summary are skipped. */
  days: AgendaDay[];
  isPublic: boolean;
}

/**
 * Renders the summary-only agenda.
 *
 * @param props - See AgendaSummaryListProps.
 * @returns The list, or null when no day has a summary.
 */
export function AgendaSummaryList({ days, isPublic }: AgendaSummaryListProps): React.ReactElement | null {
  const summaryDays = days.filter((day) => day.summary);
  if (summaryDays.length === 0) return null;

  const gridClass = isPublic
    ? "sm:grid-cols-[16rem_minmax(0,1fr)] sm:gap-8"
    : "@md:grid-cols-[16rem_minmax(0,1fr)] @md:gap-8";

  return (
    <ol className="flex list-none flex-col divide-y divide-current/10 border-b border-current/10 text-(--lp-text,#171717)">
      {summaryDays.map((day) => (
        <li key={day.date} className={`grid items-baseline gap-1.5 py-6 ${gridClass}`}>
          <h3 className="text-xl font-bold tracking-tight text-(--lp-accent,#4338ca)">
            {formatScheduleDayHeading(day.date)}
          </h3>
          <p className="whitespace-pre-line text-lg font-medium text-(--lp-text,#262626)">{day.summary}</p>
        </li>
      ))}
    </ol>
  );
}
